/**
 * Context Menu - Appears on text selection
 * Shows "Listen", "Dictionary", "Translate" options
 * Themed via ResolvedTheme
 */

import { t } from '../i18n';
import type { ResolvedTheme } from './ThemeManager';
import { ensureFocusStyles } from './a11y';

export interface ContextMenuOptions {
  theme?: ResolvedTheme;
  onListen: () => void;
  onDictionary?: (word: string) => void;
  onTranslate?: () => void;
}

export class ContextMenu {
  private element: HTMLDivElement;
  private options: ContextMenuOptions;
  private currentText: string = '';

  constructor(options: ContextMenuOptions) {
    this.options = options;
    this.element = this.createElement();
    document.body.appendChild(this.element);
  }

  private get theme(): ResolvedTheme {
    return this.options.theme || {
      primaryColor: '#1976d2',
      secondaryColor: '#f5f5f5',
      textColor: '#333333',
      textOnPrimary: '#ffffff',
      backgroundColor: '#ffffff',
      borderColor: '#e0e0e0',
      borderRadius: 12,
      mode: 'light',
    };
  }

  /**
   * Create menu element
   */
  private createElement(): HTMLDivElement {
    const th = this.theme;
    const menu = document.createElement('div');
    ensureFocusStyles();
    menu.className = 'rv-context-menu';
    menu.style.cssText = ` position: fixed; background: ${th.backgroundColor}; border: 1px solid ${th.borderColor}; border-radius: ${th.borderRadius}px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); padding: 8px; display: none; z-index: 2147483647; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; `;

    menu.innerHTML = ` <button class="rv-menu-btn rv-listen-btn" title="${t('Listen to selected text')}"> <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"> <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon> <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path> </svg> ${t('Listen')} </button> <button class="rv-menu-btn rv-ctx-dict-btn" title="${t('Dictionary')}" style="display: none;"> <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"> <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path> <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path> </svg> ${t('Define')} </button> <button class="rv-menu-btn rv-ctx-translate-btn" title="${t('Translate')}" style="display: none;"> <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"> <path d="m5 8 6 6"></path> <path d="m4 14 6-6 2-3"></path> <path d="M2 5h12"></path> <path d="M7 2h1"></path> <path d="m22 22-5-10-5 10"></path> <path d="M14 18h6"></path> </svg> ${t('Translate')} </button> `;

    // Scoped styles
    const styleId = 'rv-context-menu-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = ` .rv-menu-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 12px; border: none; background: transparent; cursor: pointer; font-size: 14px; color: ${th.textColor}; border-radius: 4px; transition: background 0.2s; } .rv-menu-btn:hover { background: ${th.secondaryColor}; } .rv-menu-btn svg { flex-shrink: 0; } .rv-listen-btn { color: ${th.primaryColor}; font-weight: 500; } .rv-listen-btn:hover { background: ${th.primaryColor}18; } `;
      document.head.appendChild(style);
    }

    // Event listeners
    const listenBtn = menu.querySelector('.rv-listen-btn') as HTMLButtonElement;
    listenBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.options.onListen();
    });

    const dictBtn = menu.querySelector('.rv-ctx-dict-btn') as HTMLButtonElement;
    if (this.options.onDictionary) {
      dictBtn.style.display = 'inline-flex';
      dictBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const word = this.getFirstWord(this.currentText);
        this.options.onDictionary?.(word);
      });
    }

    const translateBtn = menu.querySelector('.rv-ctx-translate-btn') as HTMLButtonElement;
    if (this.options.onTranslate) {
      // Hidden until the widget config confirms translation is enabled (setTranslateEnabled)
      translateBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.options.onTranslate?.();
      });
    }

    return menu;
  }

  /**
   * Show menu at position
   */
  show(options: { x: number; y: number; text: string }): void {
    this.currentText = options.text;
    document.dispatchEvent(new CustomEvent('rv-engaged'));

    this.element.style.display = 'flex';
    this.element.style.gap = '4px';

    const rect = this.element.getBoundingClientRect();
    const x = Math.max(10, Math.min(options.x - rect.width / 2, window.innerWidth - rect.width - 10));
    const y = Math.max(10, options.y - rect.height - 10);

    this.element.style.left = `${x}px`;
    this.element.style.top = `${y}px`;
  }

  /**
   * Show/hide the Translate action (driven by server feature flags)
   */
  setTranslateEnabled(enabled: boolean): void {
    const btn = this.element.querySelector('.rv-ctx-translate-btn') as HTMLButtonElement | null;
    if (btn) btn.style.display = enabled && this.options.onTranslate ? 'inline-flex' : 'none';
  }

  /**
   * Hide menu
   */
  hide(): void {
    this.element.style.display = 'none';
  }

  /**
   * Check if element contains node
   */
  contains(node: Node): boolean {
    return this.element.contains(node);
  }

  /**
   * Get first word from text
   */
  private getFirstWord(text: string): string {
    return text.split(/\s+/)[0].replace(/[^\w]/g, '');
  }

  /**
   * Destroy menu
   */
  destroy(): void {
    this.element.remove();
  }
}
